import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FaBrain, FaBolt, FaMicrochip, FaLayerGroup, FaArrowLeft, FaChartLine } from 'react-icons/fa';
import Navigation from '../Navigation/Navigation';
import Footer from '../Footer/Footer';
import SEOHead from '../SEOHead';
import OptimizedImage from '../common/OptimizedImage';

// Import feature images
import bibleAiChatImage from '../../assets/bible-chat-updated.webp';
import growthProfileImage from '../../assets/growth-profile-updated.webp';
import bibleStudyProgressImage from '../../assets/bible-study-progress.webp';

const TechnologyPage: React.FC = () => {
    const { t } = useTranslation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections = [
        {
            icon: <FaBrain className="text-4xl text-purple-500" />,
            title: t('technology.pillars.brain.title'),
            description: t('technology.pillars.brain.description'),
            points: [
                t('technology.pillars.brain.point1'),
                t('technology.pillars.brain.point2'),
                t('technology.pillars.brain.point3')
            ]
        },
        {
            icon: <FaMicrochip className="text-4xl text-blue-500" />,
            title: t('technology.pillars.speed.title'),
            description: t('technology.pillars.speed.description'),
            points: [
                t('technology.pillars.speed.point1'),
                t('technology.pillars.speed.point2'),
                t('technology.pillars.speed.point3')
            ]
        },
        {
            icon: <FaChartLine className="text-4xl text-green-500" />,
            title: t('technology.pillars.growth.title'),
            description: t('technology.pillars.growth.description'),
            points: [
                t('technology.pillars.growth.point1'),
                t('technology.pillars.growth.point2'),
                t('technology.pillars.growth.point3')
            ]
        }
    ];

    const deepDiveSteps = [
        {
            number: 1,
            color: "blue",
            title: t('technology.deepDive.step1.title'),
            description: t('technology.deepDive.step1.description')
        },
        {
            number: 2,
            color: "purple",
            title: t('technology.deepDive.step2.title'),
            description: t('technology.deepDive.step2.description')
        },
        {
            number: 3,
            color: "green",
            title: t('technology.deepDive.step3.title'),
            description: t('technology.deepDive.step3.description')
        },
        {
            number: 4,
            color: "orange",
            title: t('technology.deepDive.step4.title'),
            description: t('technology.deepDive.step4.description')
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <SEOHead />
            <Navigation />

            {/* Hero Header */}
            <div className="pt-32 pb-20 bg-black relative overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 -left-1/4 w-1/2 h-full bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
                    <div className="absolute top-0 -right-1/4 w-1/2 h-full bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
                </div>
                
                <div className="container mx-auto px-6 relative z-10 text-center">
                    <Link to="/" className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8 transition-colors">
                        <FaArrowLeft className="mr-2" /> {t('technology.hero.backToDashboard')}
                    </Link>
                    <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 uppercase tracking-tighter">
                        {t('technology.hero.title')} <span className="text-blue-500">{t('technology.hero.titleHighlight')}</span>
                    </h1>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                        {t('technology.hero.subtitle')}
                    </p>
                </div>
            </div>

            {/* Core Pillars */}
            <div className="py-20 container mx-auto px-6">
                <div className="grid md:grid-cols-3 gap-8">
                    {sections.map((section, idx) => (
                        <div key={idx} className="bg-white p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 flex flex-col h-full">
                            <div className="mb-6">{section.icon}</div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h3>
                            <p className="text-gray-600 mb-8 flex-grow">{section.description}</p>
                            <ul className="space-y-3">
                                {section.points.map((point, pIdx) => (
                                    <li key={pIdx} className="flex items-start text-sm text-gray-700">
                                        <span className="text-blue-500 mr-2">✓</span>
                                        {point}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>

            {/* Deep Dive: How Answers Are Generated */}
            <div className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('technology.deepDive.title')}</h2>
                            <p className="text-lg text-gray-600">{t('technology.deepDive.subtitle')}</p>
                        </div>

                        <div className="max-w-4xl mx-auto space-y-12 mb-24">
                            {deepDiveSteps.map((step) => (
                                <div key={step.number} className="flex gap-6">
                                    <div className={`flex-none w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold border-2 border-blue-500`}>
                                        {step.number}
                                    </div>
                                    <div>
                                        <h4 className="text-xl font-bold mb-2">{step.title}</h4>
                                        <p className="text-gray-600">{step.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Article Cards Grid - Reusing FeaturesSection Layout */}
                        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    key: 'brain',
                                    icon: '💬',
                                    image: bibleAiChatImage,
                                    to: '/learn-more-about-our-technology'
                                },
                                {
                                    key: 'growth',
                                    icon: '📈',
                                    image: growthProfileImage,
                                    to: '/growth-profile-ai-technology'
                                }
                            ].map((article) => (
                                <Link 
                                    key={article.key}
                                    to={article.to}
                                    className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 text-left flex flex-col h-full"
                                >
                                    {/* Article Image Preview */}
                                    <div className="relative h-48 overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-100">
                                        <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent z-10"></div>
                                        <OptimizedImage
                                            src={article.image}
                                            alt={`${t(`technology.articles.${article.key}.title`)} Preview`}
                                            className="group-hover:scale-110 transition-transform duration-500 filter drop-shadow-lg h-full w-full object-cover"
                                            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                        />
                                        {/* Overlay Icon */}
                                        <div className="absolute top-4 right-4 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center text-2xl shadow-lg z-20">
                                            {article.icon}
                                        </div>
                                    </div>

                                    <div className="p-6 flex flex-col h-full">
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                                            {t(`technology.articles.${article.key}.title`)}
                                        </h3>
                                        <p className="text-gray-600 mb-4 leading-relaxed text-sm flex-grow">
                                            {t(`technology.articles.${article.key}.intro`)}
                                        </p>
                                        
                                        {/* Benefits List */}
                                        <ul className="space-y-2 mb-6">
                                            {[1, 2, 3].map((num) => (
                                                <li key={num} className="flex items-center text-xs text-gray-500 group-hover:text-gray-700 transition-colors">
                                                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2 group-hover:bg-blue-600 transition-colors"></span>
                                                    {t(`technology.articles.${article.key}.benefit${num}`)}
                                                </li>
                                            ))}
                                        </ul>

                                        <div className="flex items-center text-blue-600 text-xs font-bold uppercase tracking-wider">
                                            {t('technology.articles.readMore')}
                                            <span className="ml-2 transform group-hover:translate-x-1 transition-transform">→</span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Privacy Section */}
            <div className="py-20 bg-blue-50">
                <div className="container mx-auto px-6 text-center">
                    <h2 className="text-4xl font-bold mb-8">{t('technology.privacy.title')}</h2>
                    <p className="text-xl text-gray-700 max-w-2xl mx-auto mb-12">
                        {t('technology.privacy.description')}
                    </p>
                    <div className="inline-block bg-white p-6 rounded-2xl shadow-lg border-l-4 border-blue-500">
                        <p className="font-semibold text-gray-900 text-center">"{t('technology.privacy.quote')}"</p>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default TechnologyPage;
